// SharedPostcardLandingPage.tsx

import React from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Form,
  Badge,
} from "react-bootstrap";
import {
  Envelope,
  Phone,
  People,
  Megaphone,
  GraphUpArrow,
  GeoAlt,
  CheckCircle,
  ArrowRight,
} from "react-bootstrap-icons";

export const BigPostcardLandingPage_2025_11_21_chat: React.FC = () => {
  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    // Replace with real form handling (API call, etc.)
    console.log("Form submitted");
  };

  return (
    <div style={{ backgroundColor: "#f8f9fa" }}>
      {/* HERO SECTION */}
      <section
        id="hero"
        className="py-5"
        style={{
          background: "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)",
          color: "#fff",
        }}
      >
        <Container>
          <Row className="align-items-center">
            <Col md={7} className="mb-4 mb-md-0">
              <Badge bg="light" text="dark" className="mb-3">
                Large Shared Postcards • Hyper-Local Marketing
              </Badge>
              <h1 className="display-4 fw-bold mb-3">
                Fill Your Business with New Customers Using{" "}
                <span className="text-warning">Oversized Shared Postcards</span>
              </h1>
              <p className="lead mb-4">
                We design, print, and mail jumbo postcards shared by multiple
                local businesses so you get{" "}
                <strong>massive mailbox visibility</strong> at a fraction of the
                cost of solo mailings.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <Button
                  variant="warning"
                  size="lg"
                  href="#contact"
                  className="d-flex align-items-center gap-2 fw-semibold"
                >
                  Get Pricing & Availability <ArrowRight />
                </Button>
                <Button
                  variant="outline-light"
                  size="lg"
                  href="#how-it-works"
                  className="d-flex align-items-center gap-2"
                >
                  How It Works
                  <Megaphone />
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-4 mt-4 small">
                <div className="d-flex align-items-center gap-2">
                  <CheckCircle /> No contracts
                </div>
                <div className="d-flex align-items-center gap-2">
                  <CheckCircle /> Exclusive category options
                </div>
                <div className="d-flex align-items-center gap-2">
                  <CheckCircle /> Targeted neighborhood campaigns
                </div>
              </div>
            </Col>
            <Col md={5}>
              <Card className="shadow-lg border-0">
                <Card.Body className="p-4">
                  <h5 className="text-uppercase text-muted mb-2">
                    Sample Layout
                  </h5>
                  <h3 className="fw-bold mb-3">Shared Mega Postcard</h3>
                  <div
                    style={{
                      borderRadius: "1rem",
                      overflow: "hidden",
                      border: "1px dashed #ced4da",
                      backgroundColor: "#fff",
                    }}
                  >
                    <div className="p-3 border-bottom bg-light">
                      <div className="d-flex justify-content-between small text-muted">
                        <span>Front Side • 9&quot;x12&quot;</span>
                        <span>Neighborhood Edition</span>
                      </div>
                    </div>
                    <div className="p-3">
                      <Row className="g-2">
                        <Col xs={6}>
                          <div className="p-3 bg-primary bg-opacity-10 rounded">
                            <div className="fw-bold small mb-1">
                              Premium Top Banner
                            </div>
                            <div className="small text-muted">
                              High-impact headline space.
                            </div>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="p-3 bg-warning bg-opacity-25 rounded">
                            <div className="fw-bold small mb-1">
                              Anchor Offer
                            </div>
                            <div className="small text-muted">
                              Exclusive spotlight placement.
                            </div>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="p-3 bg-success bg-opacity-10 rounded">
                            <div className="fw-bold small mb-1">
                              Business Feature Box
                            </div>
                            <div className="small text-muted">
                              Logo, offer, QR code.
                            </div>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="p-3 bg-info bg-opacity-10 rounded">
                            <div className="fw-bold small mb-1">
                              Business Feature Box
                            </div>
                            <div className="small text-muted">
                              Perfect for local services.
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                  <p className="mt-3 small text-muted mb-0">
                    Layouts customized for your brand, industry, and offer.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* VALUE PROPOSITION / FEATURES */}
      <section id="benefits" className="py-5">
        <Container>
          <Row className="mb-4">
            <Col md={8}>
              <h2 className="fw-bold mb-2">
                Why Shared Postcards Beat Solo Mailers
              </h2>
              <p className="text-muted mb-0">
                Get the size, impact, and targeting of premium direct mail
                without paying solo-mailer prices. We bundle multiple
                non-competing businesses onto one jumbo card and mail it to
                carefully chosen neighborhoods.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <People className="me-2" size={26} />
                    <h5 className="mb-0">Shared Cost, Big Impact</h5>
                  </div>
                  <Card.Text className="text-muted">
                    Split printing and postage costs with other local
                    businesses but keep your own branded ad space. Enjoy jumbo
                    postcard visibility at a fraction of the usual price.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <GraphUpArrow className="me-2" size={26} />
                    <h5 className="mb-0">Trackable Response</h5>
                  </div>
                  <Card.Text className="text-muted">
                    Build in QR codes, unique URLs, or special offer codes so
                    you can clearly see how many customers came from each
                    mailing.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <GeoAlt className="me-2" size={26} />
                    <h5 className="mb-0">Hyper-Local Targeting</h5>
                  </div>
                  <Card.Text className="text-muted">
                    Choose specific carrier routes and neighborhoods that match
                    your ideal customer profile—no wasted coverage.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section id="how-it-works" className="py-5 bg-white">
        <Container>
          <Row className="mb-4">
            <Col md={8}>
              <h2 className="fw-bold mb-2">How Our Shared Postcard Program Works</h2>
              <p className="text-muted mb-0">
                We handle everything—strategy, design, printing, and mailing.
                You focus on serving new customers.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={3} sm={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center mb-2">
                    <Badge bg="primary" className="me-2">
                      1
                    </Badge>
                    <h6 className="mb-0">Pick Your Area</h6>
                  </div>
                  <p className="small text-muted mt-2">
                    We help you select high-value routes based on income level,
                    home type, and distance from your business.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center mb-2">
                    <Badge bg="primary" className="me-2">
                      2
                    </Badge>
                    <h6 className="mb-0">Design Your Ad</h6>
                  </div>
                  <p className="small text-muted mt-2">
                    Our design team builds a compelling offer with bold
                    graphics, clear calls to action, and response tracking.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center mb-2">
                    <Badge bg="primary" className="me-2">
                      3
                    </Badge>
                    <h6 className="mb-0">We Print & Mail</h6>
                  </div>
                  <p className="small text-muted mt-2">
                    We coordinate all the other advertisers, handle printing,
                    sorting, and USPS Every Door Direct Mail® logistics.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center mb-2">
                    <Badge bg="primary" className="me-2">
                      4
                    </Badge>
                    <h6 className="mb-0">You Get New Customers</h6>
                  </div>
                  <p className="small text-muted mt-2">
                    Customers bring in your offer, scan your QR code, or call
                    your tracked number—so you can see real ROI.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* PRICING SECTION */}
      <section id="pricing" className="py-5">
        <Container>
          <Row className="mb-4 text-center">
            <Col>
              <h2 className="fw-bold mb-2">Simple, Transparent Programs</h2>
              <p className="text-muted mb-0">
                Pricing below is example-only. Request a custom quote for your
                city, volume, and industry.
              </p>
            </Col>
          </Row>
          <Row className="g-4 align-items-stretch">
            <Col md={4}>
              <Card className="h-100 shadow-sm">
                <Card.Body className="d-flex flex-column">
                  <div className="mb-3">
                    <h5 className="fw-bold">Local Starter</h5>
                    <p className="text-muted small mb-0">
                      Perfect for first-time mailers.
                    </p>
                  </div>
                  <div className="mb-3">
                    <span className="h3 fw-bold">$</span>
                    <span className="h1 fw-bold">Low</span>
                    <span className="text-muted"> / drop</span>
                  </div>
                  <ul className="list-unstyled small mb-4">
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> One shared postcard zone
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> Professional ad design
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> Basic response tracking
                    </li>
                  </ul>
                  <Button
                    variant="outline-primary"
                    href="#contact"
                    className="mt-auto"
                  >
                    Request Quote
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 shadow-lg border-primary">
                <Card.Body className="d-flex flex-column">
                  <Badge bg="primary" className="align-self-start mb-2">
                    Most Popular
                  </Badge>
                  <div className="mb-3">
                    <h5 className="fw-bold">Neighborhood Pro</h5>
                    <p className="text-muted small mb-0">
                      Consistent mailings that keep you top-of-mind.
                    </p>
                  </div>
                  <div className="mb-3">
                    <span className="h3 fw-bold">$</span>
                    <span className="h1 fw-bold">Better</span>
                    <span className="text-muted"> / drop</span>
                  </div>
                  <ul className="list-unstyled small mb-4">
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> Multiple zones available
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> Priority ad placement
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> Upgraded tracking & QR
                      codes
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> Campaign performance
                      review
                    </li>
                  </ul>
                  <Button variant="primary" href="#contact" className="mt-auto">
                    Talk to a Specialist
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 shadow-sm">
                <Card.Body className="d-flex flex-column">
                  <div className="mb-3">
                    <h5 className="fw-bold">City-Wide Domination</h5>
                    <p className="text-muted small mb-0">
                      Designed for aggressive growth.
                    </p>
                  </div>
                  <div className="mb-3">
                    <span className="h3 fw-bold">Custom</span>
                    <span className="text-muted"> pricing</span>
                  </div>
                  <ul className="list-unstyled small mb-4">
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> Multi-drop annual plans
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> Exclusive categories
                      available
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> Advanced targeting &
                      testing
                    </li>
                    <li className="d-flex align-items-center mb-2">
                      <CheckCircle className="me-2" /> Dedicated account
                      manager
                    </li>
                  </ul>
                  <Button
                    variant="outline-primary"
                    href="#contact"
                    className="mt-auto"
                  >
                    Schedule Strategy Call
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-5 bg-white">
        <Container>
          <Row className="mb-4">
            <Col md={8}>
              <h2 className="fw-bold mb-2">What Local Businesses Are Saying</h2>
              <p className="text-muted mb-0">
                Shared postcards work best when they highlight complementary
                businesses your neighbors actually use.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6}>
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <p className="mb-3">
                    “Our phones lit up within days of the postcard landing.
                    We&apos;ve tested Facebook, Google, and door hangers—this
                    was the fastest response we&apos;ve seen in years.”
                  </p>
                  <div className="small">
                    <strong>Sarah P.</strong> • Owner, Fresh &amp; Clean Carpet
                    Care
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <p className="mb-3">
                    “Love that we can afford a big, bold postcard without doing
                    it alone. Being next to other high-quality businesses adds
                    instant credibility.”
                  </p>
                  <div className="small">
                    <strong>Michael R.</strong> • Partner, Neighborhood Auto
                    Repair
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CONTACT / LEAD FORM */}
      <section
        id="contact"
        className="py-5"
        style={{ backgroundColor: "#0b1727", color: "#fff" }}
      >
        <Container>
          <Row className="mb-4">
            <Col md={7}>
              <h2 className="fw-bold mb-2">Get a Custom Quote</h2>
              <p className="text-light mb-1">
                Tell us a bit about your business and what areas you&apos;d like
                to reach. We&apos;ll send back a sample layout, estimated
                pricing, and upcoming mailing dates.
              </p>
              <div className="d-flex flex-wrap gap-4 mt-3 small">
                <div className="d-flex align-items-center gap-2">
                  <Phone /> <span>Call: (555) 123-4567</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Envelope /> <span>info@sharedpostcardpros.com</span>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={7} className="mb-4 mb-md-0">
              <Card className="border-0 shadow-lg">
                <Card.Body className="p-4">
                  <h5 className="mb-3">Start Your Shared Postcard Campaign</h5>
                  <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col sm={6}>
                        <Form.Group controlId="contactName">
                          <Form.Label>Full Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Jane Businessowner"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="businessName">
                          <Form.Label>Business Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Your Business Name"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="email">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            placeholder="you@example.com"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="phone">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            type="tel"
                            placeholder="(555) 123-4567"
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="website">
                          <Form.Label>Website (optional)</Form.Label>
                          <Form.Control
                            type="url"
                            placeholder="https://yourbusiness.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="monthlyBudget">
                          <Form.Label>Approx. Monthly Mail Budget</Form.Label>
                          <Form.Select defaultValue="">
                            <option value="" disabled>
                              Select a range
                            </option>
                            <option value="<1000">Under $1,000</option>
                            <option value="1000-2500">$1,000 – $2,500</option>
                            <option value="2500-5000">$2,500 – $5,000</option>
                            <option value=">5000">Over $5,000</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group controlId="areas">
                          <Form.Label>
                            Which neighborhoods or cities are you targeting?
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Tell us about your ideal customers and where they live."
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group controlId="message">
                          <Form.Label>Anything else we should know?</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Current offers, upcoming promotions, timelines, etc."
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} className="d-flex justify-content-between align-items-center mt-2">
                        <div className="small text-muted">
                          We&apos;ll respond within one business day with
                          recommended options.
                        </div>
                        <Button type="submit" variant="primary">
                          Get My Quote
                        </Button>
                      </Col>
                    </Row>
                    {submitted && (
                      <div className="mt-3 small text-success d-flex align-items-center gap-2">
                        <CheckCircle /> Thanks! Your request has been received.
                      </div>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
            <Col md={5}>
              <div className="h-100 d-flex flex-column justify-content-center">
                <h6 className="text-uppercase text-light-50 mb-3">
                  Ideal for Local:
                </h6>
                <ul className="list-unstyled small">
                  <li>• Home services (HVAC, plumbing, landscaping, cleaning)</li>
                  <li>• Restaurants &amp; coffee shops</li>
                  <li>• Auto repair, detail, &amp; car wash</li>
                  <li>• Dentists, chiropractors, and health clinics</li>
                  <li>• Retail shops &amp; boutiques</li>
                  <li>• Gyms, studios, and wellness centers</li>
                </ul>
                <p className="small text-light mt-3 mb-0">
                  Not sure if shared postcards are right for you? Send us your
                  website and ideal customer and we&apos;ll give you an honest
                  recommendation.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* SIMPLE FOOTER */}
      <footer className="py-3 bg-dark text-light small">
        <Container className="d-flex flex-wrap justify-content-between align-items-center">
          <div>
            &copy; {new Date().getFullYear()} Shared Postcard Pros. All rights
            reserved.
          </div>
          <div className="d-flex gap-3">
            <a href="#hero" className="text-decoration-none text-light">
              Back to top
            </a>
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default BigPostcardLandingPage_2025_11_21_chat;
